/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { JobStatus, JobType, TaskStatus, TaskType } from './background-jobs';
import { DataPeriods } from '@utils/dataPeriods';
import type { UniversalTrackerPlain } from './universalTracker';
import type { ValueListPlain } from './valueList';

export enum DataTransferMappingStatus {
  Pending = 'pending',
  Active = 'active',
  NoData = 'no_data',
}

export enum SurveyDataStatus {
  Value = 'value',
  Na = 'na',
  NoSurvey = 'no_survey',
  NotAnswered = 'not_answered',
}

export enum IntegrationValueSource {
  Connection = 'connection',
  ApiPassthrough = 'api_passthrough',
  StaticValue = 'static_value',
  ClientOverride = 'client_override',
  SubsidiaryOverride = 'subsidiary_override',
  Derived = 'derived',
}

export interface DataTransferConnectionInfo {
  connectionCode: string;
  calculationName: string;
  variableCode: string;
  valueListCode: string;
}

export interface DataTransferValue {
  value?: string | number;
  unit?: string;
  numberScale?: string;
  /** Explanation note — written to utrv.note when applying */
  note?: string;
}

export interface DataTransferRow {
  utrCode: string;
  optionCode?: string;
  tableRowMatch?: Array<{ code: string; value: string }>;
  utrLabel: string;
  optionLabel?: string;
  valueType: string;
  connectionInfo?: DataTransferConnectionInfo;
  survey: DataTransferValue & {
    status: SurveyDataStatus;
    utrvId?: string;
    utrvStatus?: string;
    surveyId?: string;
  };
  integration: DataTransferValue & {
    source?: IntegrationValueSource;
    detail?: string;
  };
  status: DataTransferMappingStatus;
  verifiedAt?: string;
  hasDiscrepancy: boolean;
  toleranceResult?: ToleranceResult;
  notices: ValidationNotice[];
  internalNotes?: string;
  partnerNotes?: string;
}

export enum ValidationNoticeType {
  Info = 'info',
  Warning = 'warning',
  Error = 'error',
}

export interface ValidationNotice {
  type: ValidationNoticeType;
  code: string;
  message: string;
}

export interface DataTransferSummary {
  total: number;
  comparable: number;
  matching: number;
  differing: number;
  pending: number;
  noData: number;
}

export interface DataTransferSurveyResult {
  surveyId: string;
  label: string;
  effectiveDate: string;
  rows: DataTransferRow[];
  summary: DataTransferSummary;
}

export interface DataTransferResponse {
  surveys: DataTransferSurveyResult[];
  connectionExternalId?: string;
  childExternalId?: string;
  notices: ValidationNotice[];
}

export interface DataTransferTolerance {
  type: 'percentage' | 'absolute';
  value: number;
}

export interface ToleranceResult {
  tolerance: DataTransferTolerance;
  actualDifference: number;
}

export type GroupMode = 'survey' | 'question';

// --- Data Transfer Sync types ---

export enum RowApprovalStatus {
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected',
  Skipped = 'skipped',
  Flagged = 'flagged',
}

export type DataTransferFieldValue = string | number | string[];

export interface DataTransferFieldSnapshot {
  value?: DataTransferFieldValue;
  unit?: string;
  numberScale?: string;
}

export interface DataTransferMetricValue {
  survey: DataTransferFieldSnapshot;
  integration: DataTransferFieldSnapshot & { source?: IntegrationValueSource; detail?: string };
  hasDiscrepancy: boolean;
  toleranceResult?: { tolerance: { type: 'percentage' | 'absolute'; value: number }; actualDifference: number };
}

export interface DataTransferMetricEntry {
  key: string;
  optionCode?: string;
  tableRowMatch?: Array<{ code: string; value: string }>;
  survey?: DataTransferFieldSnapshot;
  integration?: DataTransferFieldSnapshot & { source?: IntegrationValueSource; detail?: string };
  hasDiscrepancy: boolean;
  toleranceResult?: { tolerance: { type: 'percentage' | 'absolute'; value: number }; actualDifference: number };
}

export interface DataTransferReview {
  approval?: RowApprovalStatus;
  entries?: Array<{ key: string; approval: RowApprovalStatus }>;
  reviewedBy: string;
  reviewedAt: string;
}

export type DataTransferSyncResult = {
  status: 'synced';
  historyId: string;
  syncedAt: string;
  retryCount?: number;
} | {
  status: 'error';
  error: string;
  entryKey?: string;
  syncedAt: string;
  retryCount: number;
};

export interface DataTransferMetricTaskData {
  utrCode: string;
  integrationCode: string;
  utrvId?: string;
  utrvHistoryId?: string;
  surveyId?: string;
  surveyStatus: SurveyDataStatus;
  snapshotAt: string;
  mappingStatus: DataTransferMappingStatus;
  autoClassification: RowApprovalStatus;
  notices: ValidationNotice[];
  value?: DataTransferMetricValue;
  entries?: DataTransferMetricEntry[];
  reviews?: DataTransferReview[];
  sync?: DataTransferSyncResult;
}

export interface DataTransferTask {
  id: string;
  type: TaskType;
  name: string;
  status: TaskStatus;
  parentTaskId?: string;
  data: DataTransferMetricTaskData;
}

export interface DataTransferJob {
  _id: string;
  type: JobType;
  status: JobStatus;
  tasks: DataTransferTask[];
  created: string;
  updated: string;
}

export interface DataTransferReport {
  initiativeId: string;
  jobId: string;
}

export interface DataTransferPlain {
  _id: string;
  initiativeId: string;
  integrationCode: string;
  name: string;
  startDate: string;
  endDate: string;
  frequency: DataPeriods;
  reports: DataTransferReport[];
  createdBy: string;
  created: string;
  updated: string;
}

export interface DataTransferDashboardResponse {
  dataTransfer: DataTransferPlain;
  jobs: DataTransferJob[];
}

export interface DataTransferUtrsResponse {
  utrs: UniversalTrackerPlain[];
  valueLists: ValueListPlain[];
}
